/**
 * `ProductManager` orchestrates the product selection page.
 *
 * Responsibilities:
 * - Fetches available products via `ProductApi.getProducts` when not already
 *   present.
 * - Applies quote-derived defaults when `showQuotedPrice` is enabled
 *   (pre-selects products/variants that have existing quoted prices).
 * - Displays quote-expired and invalid-combination modals.
 * - Prevents invalid product combinations using `checkAddProductPlanConflict`
 *   and `getInvalidProductTypeCombinations`.
 * - Navigates back/continue based on journey type and context.
 * - Translates document names before passing them up via
 *   `onUpdateProductDocuments`.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProductApi } from '../../../api/ProductApi';
import {
  PageAbsolutePath,
  PageRelativePath,
  VehicleIdType
} from '../../../consts/Common';
import { PaymentMethod } from '../Constants';
import PageHeader from '../PageHeader';
import {
  DocumentTreeItem,
  ProductData,
  ProductFilter,
  ProductPageCustomisation,
  ProductType,
  QuoteProduct,
  VinWarrantyExpiry
} from '../CommonTypes';
import Products from './Products';
import { ButtonContinue } from '../Button/ButtonContinue';
import { Journey } from '../../../consts/Common';
import { ButtonBack } from '../Button/ButtonBack';
import { ButtonIcon } from '../Button/ButtonIcon';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { ButtonConfirm } from '../Button/ButtonConfirm';
import LoadingContainer from '../LoadingContainer/LoadingContainer';
import { useTranslation } from 'react-i18next';
import { setCountryCode } from '../HelperMethods';
import {
  getQuotedPrice,
  getInvalidProductTypeCombinations,
  checkAddProductPlanConflict
} from '../ProductMethods';
import { Helmet } from 'react-helmet';
import { useAppSelector } from '../../../app-state/Hooks';
import { translateDocumentNames } from '../../../helpers/documentsTranslation';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

type ProductManagerProps = {
  products: ProductType[];
  productFilter: ProductFilter;
  foundExistingQuotes: boolean;
  cms: ProductPageCustomisation;
  onUpdateProducts: (
    products: ProductType[],
    updateDocuments?: boolean
  ) => void;
  onUpdateProductDocuments: (documents: DocumentTreeItem[]) => void;
  onUpdateManufacturingCoverageMonths: (months: number) => void;
  token: string;
  quoteProducts?: QuoteProduct[];
  hasQuoteExpired: boolean;
  isExpiredMessageAcknowledged: boolean;
  setIsExpiredMessageAcknowledged: (
    isExpiredMessageAcknowledged: boolean
  ) => void;
  showKeyBenefits: boolean;
  showQuotedPrice: boolean;
  expiryDetails?: VinWarrantyExpiry;
  vehicleIdType?: VehicleIdType;
  showKeyBenefitsInProductCard?: boolean;
};

const ProductManager = ({
  products,
  productFilter,
  foundExistingQuotes,
  cms,
  onUpdateProducts,
  onUpdateProductDocuments,
  onUpdateManufacturingCoverageMonths,
  token,
  quoteProducts,
  hasQuoteExpired,
  isExpiredMessageAcknowledged,
  setIsExpiredMessageAcknowledged,
  showKeyBenefits,
  showQuotedPrice,
  expiryDetails,
  vehicleIdType,
  showKeyBenefitsInProductCard
}: ProductManagerProps): JSX.Element => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [invalidProductError, setInvalidProductError] = useState({
    message: ''
  });
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const appConfig = useAppSelector((s) => s.appConfig);

  const onCloseModal = () => {
    setShowModal(false);
    setIsExpiredMessageAcknowledged(true);
  };

  const noOfInstallments = Math.max(
    ...products
      .map((p) => p.productVariants)
      .reduce((a, b) => a.concat(b), [])
      .filter((v) => v.paymentMethod === PaymentMethod.Monthly)
      .map((v) => v.noOfInstallments),
    0
  );

  useEffect(() => {
    window.scrollTo(0, 0);

    if (hasQuoteExpired && !isExpiredMessageAcknowledged) {
      setShowModal(true);
    }

    getProducts();
  }, []);

  const makeDefaultSelection = (products: ProductType[]) => {
    if (products.length === 1) {
      const newProducts = products.map((p) => {
        return { ...p, isSelected: true };
      });
      onUpdateProducts(newProducts, true);
    }
  };

  const getProducts = () => {
    const controller = new AbortController();

    const successCallback = (productDetails: ProductData) => {
      const updatedProductDetails = productDetails.products.map((product) => {
        const quotePrice = getQuotedPrice(product, quoteProducts ?? []);
        return {
          ...product,
          isSelected: showQuotedPrice && quotePrice !== undefined,
          productVariants: product.productVariants.map((variant) => ({
            ...variant,
            isSelected: quotePrice?.variantId === variant.variantId
          }))
        };
      });

      const translatedDocuments = translateDocumentNames(
        productDetails.documents
      );

      onUpdateProductDocuments(translatedDocuments);
      onUpdateProducts(updatedProductDetails, true);
      onUpdateManufacturingCoverageMonths(
        productDetails.manufacturingCoverageMonths || 0
      );
      makeDefaultSelection(productDetails.products);
      setLoading(false);
    };

    const failureCallback = () => {
      setLoading(false);
    };

    if (products.length === 0) {
      if (foundExistingQuotes && quoteProducts?.length === 0) return;

      setLoading(true);

      new ProductApi().getProducts(
        productFilter,
        token,
        successCallback,
        failureCallback,
        controller.signal
      );
    }

    return () => {
      controller.abort();
    };
  };

  const addOrRemoveProduct = (productTypeCode: string) => {
    const conflict = checkAddProductPlanConflict(productTypeCode, products);

    if (conflict) {
      setInvalidProductError({
        message: t(
          'customer-product-page.conflicted-product-combination-error',
          {
            combinedProduct: conflict.combined.productName,
            singleProduct: conflict.single.productName
          }
        )
      });
      return;
    }
    const newItems = products.map((product) =>
      product.productTypeCode === productTypeCode
        ? {
            ...product,
            isSelected: !product.isSelected,
            policyStartDate: undefined,
            selectedTyreTypeId: undefined,
            selectedClaimLimit: undefined,
            selectedCoverLevelId: undefined,
            selectedPaymentMethod: undefined,
            selectedExcess: undefined,
            productVariants: product.productVariants.map((variant) =>
              variant.isSelected === true && !quoteProducts
                ? { ...variant, isSelected: false }
                : variant
            )
          }
        : product
    );

    onUpdateProducts(newItems, true);
  };

  const goToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const onBack = () => {
    if (appConfig.journeyType === Journey.Customer) {
      navigate(PageRelativePath.VehicleDetails, {
        state: { vehicleIdType: vehicleIdType }
      });
    } else if (
      appConfig.journeyType === Journey.DealerJourney &&
      foundExistingQuotes &&
      !(quoteProducts && quoteProducts.length > 0)
    ) {
      const path = setCountryCode(
        PageAbsolutePath.Landing,
        appConfig.countryCode
      );
      navigate(path);
    } else {
      navigate(PageRelativePath.VehicleDetails);
    }

    goToTop();
  };

  const onCreatePlan = () => {
    const invalidCombinations = getInvalidProductTypeCombinations(products);

    if (invalidCombinations.length > 0) {
      const invalidProductTypeCode = invalidCombinations[0]; // For simplicity, just take the first invalid combination
      setInvalidProductError({
        message: t('customer-product-page.invalid-product-combination-error', {
          combinedProduct:
            products.find(
              (p) => p.productTypeCode === invalidProductTypeCode[0]
            )?.productName || ''
        })
      });

      return;
    } else {
      setInvalidProductError({ message: '' });
    }

    if (appConfig.journeyType === Journey.Customer) {
      navigate(PageRelativePath.CreatePlan, {
        state: {
          expiryDetails: expiryDetails,
          vehicleIdType: vehicleIdType
        }
      });
    } else {
      navigate(PageRelativePath.CreatePlan);
    }
  };

  const isCreatePlanEnabled = () =>
    products.filter((p) => p.isSelected === true).length > 0;

  const informationText = cms?.information
    ? cms.information.replace('{NoOfInstalments}', noOfInstallments.toString())
    : '';

  const { t } = useTranslation();

  return (
    <LoadingContainer
      loading={loading}
      message={t('loading-placeholder.loading-text')}>
      <Helmet>
        <title>{t('page-title.products')}</title>
      </Helmet>
      <div>
        {products && products.length > 0 ? (
          <PageHeader
            title={cms.title}
            description={cms.description}
            information={informationText || ''}
            warning={cms.warning}
            descriptionClass='p2'
          />
        ) : (
          <div dangerouslySetInnerHTML={{ __html: cms.noProductsFound }} />
        )}
        {foundExistingQuotes && quoteProducts && quoteProducts.length > 0 && (
          <p className='p2'>
            {t('customer-product-page.existing-quotes-found')}
          </p>
        )}
        <Products
          products={products}
          quoteProducts={quoteProducts}
          addOrRemoveProduct={addOrRemoveProduct}
          cms={cms}
          culture={appConfig.cultureCode}
          showKeyBenefits={showKeyBenefits}
          showKeyBenefitsInProductCard={showKeyBenefitsInProductCard}
          showQuotedPrice={showQuotedPrice}
        />
        <div className='flex-row form-nav'>
          <ButtonBack
            name='btnBack'
            onClick={onBack}
            text={t('customer-product-page.back-button-text')}
          />

          <ButtonContinue
            name='btnCreatePlan'
            text={t('customer-product-page.continue-button-text')}
            onClick={onCreatePlan}
            disabled={!isCreatePlanEnabled()}
          />
          <Modal isOpen={showModal}>
            <ModalHeader
              toggle={onCloseModal}
              close={
                <ButtonIcon
                  icon={faXmark}
                  size='xs'
                  className='close'
                  onClick={onCloseModal}
                  ariaLabel={t('button-text-close')}
                />
              }
              className='modal-header-title cms-modal-header-title justify-content-between'>
              <span>{t('customer-product-page.quote-expired-heading')}</span>
            </ModalHeader>
            <ModalBody className='show-more-container'>
              <p
                className='p2 ml-15'>
                {t('customer-product-page.quote-expired-recalculate')}
              </p>
            </ModalBody>
            <ModalFooter>
              <div className='flex-row button-row'>
                <ButtonConfirm
                  name='btn-confirm-manual-vehicle'
                  text={t(
                    'customer-product-page.button-quote-expired-continue'
                  )}
                  additionalClassNames='m05-left'
                  onClick={onCloseModal}
                />
              </div>
            </ModalFooter>
          </Modal>
          <Modal isOpen={!!invalidProductError.message}>
            <ModalBody className='show-more-container'>
              <p
                className='p2 ml-15'>
                {invalidProductError.message.replace('&amp;', '&')}
              </p>
            </ModalBody>
            <ModalFooter>
              <div className='flex-row button-row'>
                <ButtonConfirm
                  name='btn-confirm-invalid-product-combination'
                  text={t('customer-product-page.button-text-close')}
                  additionalClassNames='m05-left'
                  onClick={() => {
                    setInvalidProductError({ message: '' });
                  }}
                />
              </div>
            </ModalFooter>
          </Modal>
        </div>
      </div>
    </LoadingContainer>
  );
};

export default ProductManager;
